/**
 * Per-install distribution marker (DICTATE-BUNDLE.md).
 *
 * `isDictateBundle` flips to `false` when a dictate install auto-updates to a
 * regular build, so the tag is persisted to disk (Rust-owned file, stamped by
 * Rust on startup — see ipc/distribution_tag.rs) and features gate on the
 * resolved value (baked OR persisted) to keep the dictate experience across
 * that swap. The webview only **reads** the marker over IPC, so hydration is
 * async — wait on {@link distributionTagLoadedAtom} before acting on the
 * persisted value. Per-install, not per-account.
 */
import { getDistributionTag } from '@/tauri/distribution-tag';
import { isDictateBundle } from '@/utils/buildProfile';
import { atom, getDefaultStore } from 'jotai';

export type DistributionTag = 'dictate' | null;

/**
 * In-memory cache of the on-disk marker. `undefined` = not yet read from disk;
 * `null` = read, but this install was never stamped; `'dictate'` = stamped.
 * Private — consumers use {@link isDictateExperienceAtom} / the loaded gate.
 */
const distributionTagAtom = atom<DistributionTag | undefined>(undefined);
distributionTagAtom.debugLabel = 'distributionTagAtom';

/** True once the on-disk marker has been read (the async hydration gate). */
export const distributionTagLoadedAtom = atom(
  (get) => get(distributionTagAtom) !== undefined
);
distributionTagLoadedAtom.debugLabel = 'distributionTagLoadedAtom';

/**
 * True when it is safe to branch on the persisted marker. Dictate builds resolve
 * synchronously via {@link isDictateBundle}; converted regular builds must wait
 * for {@link distributionTagLoadedAtom}.
 */
export const isDistributionTagReadyAtom = atom(
  (get) => isDictateBundle || get(distributionTagLoadedAtom)
);
isDistributionTagReadyAtom.debugLabel = 'isDistributionTagReadyAtom';

/**
 * In-flight read promise, so concurrent triggers (React 18 StrictMode
 * double-invokes the launch effect) share one IPC round-trip instead of each
 * issuing a duplicate disk read. Module-scoped, not an atom — it's transient
 * plumbing, never rendered.
 */
let inFlightLoad: Promise<void> | null = null;

/**
 * Read-only: hydrate the marker from disk into the in-memory cache, once.
 * Idempotent and safe to call on every launch — subsequent calls no-op once the
 * value is cached, and concurrent calls share {@link inFlightLoad}. Stamping is
 * owned by Rust (startup), so this never writes.
 */
export const loadDistributionTagAtom = atom(null, async (get, set) => {
  if (get(distributionTagAtom) !== undefined) return;
  if (!inFlightLoad) {
    inFlightLoad = getDistributionTag()
      .then((raw) => {
        set(distributionTagAtom, raw === 'dictate' ? 'dictate' : null);
      })
      .finally(() => {
        inFlightLoad = null;
      });
  }
  await inFlightLoad;
});
loadDistributionTagAtom.debugLabel = 'loadDistributionTagAtom';

/**
 * Resolved gate — the value feature code should branch on. True when the
 * running binary is a dictate build, or this install was stamped 'dictate' on
 * a prior launch and has since updated to a regular build.
 */
export const isDictateExperienceAtom = atom(
  (get) => isDictateBundle || get(distributionTagAtom) === 'dictate'
);
isDictateExperienceAtom.debugLabel = 'isDictateExperienceAtom';

/** Imperative equivalent of {@link isDictateExperienceAtom} for non-React gates. */
export const getIsDictateExperience = (): boolean =>
  isDictateBundle || getDefaultStore().get(distributionTagAtom) === 'dictate';
